import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { operatorRequiresValue } from '../filters/operators';

/**
 * ActiveFiltersBar Component
 * Displays active filters as chips with close icons below the toolbar
 */
function ActiveFiltersBar({ configss = [] }) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get operator labels mapping
  const operatorLabels = {
    'equals': '=',
    'not_equals': '≠',
    'contains': 'contains',
    'not_contains': 'not contains',
    'starts_with': 'starts with',
    'ends_with': 'ends with',
    'greater_than': '>',
    'greater_than_or_equal': '≥',
    'less_than': '<',
    'less_than_or_equal': '≤',
    'in': 'in',
    'not_in': 'not in',
    'between': 'between',
    'not_between': 'not between',
    'is_null': 'is null',
    'is_not_null': 'is not null',
    'today': 'is today',
    'this_week': '-This week',
    'this_month': '-This month',
    'this_month_anniversary': '-Anniversary this month',
    'this_month_joining': '-Joined this month',
    'next_30_days': '-Upcoming 30 days',
    'next_30_days_anniversary': '-Upcoming anniversaries (30 days)',
    'on': 'on',
    'date_equals': '=',
    'before': 'before',
    'date_before': 'before',
    'after': 'after',
    'date_after': 'after',
  };

  // Extract active filters from URL params
  const activeFilters = useMemo(() => {
    const filters = [];
    const processedFields = new Set();

    searchParams.forEach((value, key) => {
      // Skip operator params - they'll be processed with their field
      if (key.endsWith("_operator")) return;

      if (key.startsWith("filter_")) {
        const field = key.replace("filter_", "");

        // Skip if already processed
        if (processedFields.has(field)) return;
        processedFields.add(field);

        // Get operator from URL
        const operatorKey = `filter_${field}_operator`;
        const operator = searchParams.get(operatorKey) || 'contains';
        const requiresValue = operatorRequiresValue(operator);

        // For operators that don't require values, show filter even if value is empty
        // For operators that require values, only show if value exists
        if (!requiresValue || value) {
          // Get field label from configss
          const fieldConfig = configss.find(col => col.field === field);
          const fieldLabel = fieldConfig?.label || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

          // Format value for display
          let displayValue = value || '';
          if (operator === 'in' || operator === 'not_in') {
            displayValue = value ? value.split(',').map(v => v.trim()).join(', ') : '';
          } else if (operator === 'between' || operator === 'not_between') {
            displayValue = value ? value.split(',').map(v => v.trim()).join(' - ') : '';
          }

          filters.push({
            field,
            fieldLabel,
            operator,
            value: displayValue,
            operatorKey,
          });
        }
      }
    });

    return filters;
  }, [searchParams, configss]);

  // Handle removing a single filter
  const handleRemoveFilter = (field, operatorKey) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(`filter_${field}`);
    if (newSearchParams.has(operatorKey)) {
      newSearchParams.delete(operatorKey);
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  // Handle removing all filters
  const handleClearAllFilters = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    searchParams.forEach((value, key) => {
      if (key.startsWith("filter_")) {
        newSearchParams.delete(key);
      }
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  // Don't render if no active filters
  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <div className="ems-chips-row" style={{ marginBottom: 16 }}>
      <span
        style={{
          color: "var(--ems-muted)",
          fontWeight: 650,
          fontSize: 13,
          marginRight: 4,
        }}
      >
        Active Filters:
      </span>
      {activeFilters.map((filter, index) => {
        const requiresValue = operatorRequiresValue(filter.operator);
        const labelText = requiresValue
          ? `${filter.fieldLabel} ${operatorLabels[filter.operator] || filter.operator} ${filter.value}`
          : `${filter.fieldLabel} ${operatorLabels[filter.operator] || filter.operator}`;

        return (
          <button
            key={`${filter.field}-${index}`}
            type="button"
            className="ems-chip"
            onClick={() => handleRemoveFilter(filter.field, filter.operatorKey)}
            title="Remove filter"
          >
            {labelText}
            <X size={14} />
          </button>
        );
      })}
      {activeFilters.length > 1 && (
        <button
          type="button"
          className="ems-chip"
          style={{
            color: "var(--ems-primary)",
            borderColor: "var(--ems-primary)",
            background: "var(--ems-primary-tint)",
          }}
          onClick={handleClearAllFilters}
        >
          Clear All
        </button>
      )}
    </div>
  );
}

export default ActiveFiltersBar;

